import { useEffect, useState, type FormEvent } from "react";
import type { Locale } from "@zoya/i18n";
import {
  addToWishlist,
  deleteMyReview,
  getMyReview,
  getProduct,
  getProductReviews,
  getWishlist,
  removeFromWishlist,
  saveMyReview,
  type MyReview,
  type ProductReviews,
} from "./api.js";
import type { PublicCatalogProduct } from "./types.js";

const copy = {
  en: {
    save: "Save to wishlist",
    saved: "Saved to wishlist",
    signInToSave: "Sign in to save",
    wishlistError: "Could not update your wishlist. Try again.",
    reviewsTitle: "Customer reviews",
    noReviews: "No reviews yet.",
    reviewCount: (count: number) =>
      count === 1 ? "1 review" : `${count} reviews`,
    outOf: "out of 5",
    loadError: "Reviews could not be loaded.",
    retry: "Retry",
    yourReview: "Your review",
    rating: "Rating",
    star: (value: number) => (value === 1 ? "1 star" : `${value} stars`),
    titleLabel: "Title (optional)",
    bodyLabel: "Your review",
    submit: "Submit review",
    update: "Update review",
    remove: "Delete my review",
    submitting: "Saving…",
    thanks: "Thanks — your review is saved.",
    removed: "Your review was deleted.",
    hidden: "This review is hidden by our team and isn’t shown publicly.",
    signInToReview: "Sign in to review products you have bought.",
    signIn: "Sign in",
    purchaseRequired: "Only customers who ordered this product can review it.",
    saveError: "Your review could not be saved. Check the fields and retry.",
    wishlistTitle: "Wishlist",
    wishlistEmpty: "Your wishlist is empty.",
    browse: "Browse the catalog",
    removeFromWishlist: "Remove",
    view: "View",
    loadingWishlist: "Loading your wishlist…",
    wishlistLoadError: "Your wishlist could not be loaded.",
  },
  ar: {
    save: "أضف إلى المفضلة",
    saved: "في المفضلة",
    signInToSave: "سجّل الدخول للحفظ",
    wishlistError: "تعذر تحديث المفضلة. حاول مرة أخرى.",
    reviewsTitle: "آراء العملاء",
    noReviews: "لا توجد تقييمات بعد.",
    reviewCount: (count: number) =>
      count === 1 ? "تقييم واحد" : `${count} تقييمات`,
    outOf: "من 5",
    loadError: "تعذر تحميل التقييمات.",
    retry: "إعادة المحاولة",
    yourReview: "تقييمك",
    rating: "التقييم",
    star: (value: number) => (value === 1 ? "نجمة واحدة" : `${value} نجوم`),
    titleLabel: "العنوان (اختياري)",
    bodyLabel: "تقييمك",
    submit: "إرسال التقييم",
    update: "تحديث التقييم",
    remove: "حذف تقييمي",
    submitting: "جارٍ الحفظ…",
    thanks: "شكرًا — تم حفظ تقييمك.",
    removed: "تم حذف تقييمك.",
    hidden: "أخفى فريقنا هذا التقييم ولا يظهر للعامة.",
    signInToReview: "سجّل الدخول لتقييم المنتجات التي اشتريتها.",
    signIn: "تسجيل الدخول",
    purchaseRequired: "يمكن فقط للعملاء الذين طلبوا هذا المنتج تقييمه.",
    saveError: "تعذر حفظ تقييمك. راجع الحقول وحاول مجددًا.",
    wishlistTitle: "المفضلة",
    wishlistEmpty: "قائمة المفضلة فارغة.",
    browse: "تصفّح الكتالوج",
    removeFromWishlist: "إزالة",
    view: "عرض",
    loadingWishlist: "جارٍ تحميل المفضلة…",
    wishlistLoadError: "تعذر تحميل المفضلة.",
  },
} as const;

const loginPath = (returnTo: string) =>
  `/login?returnTo=${encodeURIComponent(returnTo)}`;

const isSessionError = (error: unknown) =>
  error instanceof Error && error.message === "CUSTOMER_SESSION_INVALID";

function Stars({ value, label }: { value: number; label: string }) {
  return (
    <span className="engagement-stars" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} aria-hidden="true" data-filled={star <= value}>
          ★
        </span>
      ))}
    </span>
  );
}

export function WishlistButton({
  slug,
  locale,
  navigate,
}: {
  slug: string;
  locale: Locale;
  navigate: (path: string) => void;
}) {
  const t = copy[locale];
  const [state, setState] = useState<
    "loading" | "anonymous" | "saved" | "unsaved"
  >("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setState("loading");
    getWishlist()
      .then((value) => {
        if (!active) return;
        if (!value.authenticated) setState("anonymous");
        else
          setState(
            value.items.some((item) => item.slug === slug)
              ? "saved"
              : "unsaved",
          );
      })
      .catch(() => {
        if (active) setState("anonymous");
      });
    return () => {
      active = false;
    };
  }, [slug]);
  const toggle = async () => {
    if (state === "anonymous") {
      navigate(loginPath(`/products/${slug}`));
      return;
    }
    if (busy || state === "loading") return;
    setBusy(true);
    setError(false);
    try {
      if (state === "saved") await removeFromWishlist(slug);
      else await addToWishlist(slug);
      setState(state === "saved" ? "unsaved" : "saved");
    } catch (failure) {
      if (isSessionError(failure)) setState("anonymous");
      else setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wishlist-action">
      <button
        type="button"
        className="button button--outline wishlist-button"
        aria-pressed={state === "saved"}
        disabled={busy || state === "loading"}
        onClick={() => void toggle()}
      >
        <span aria-hidden="true">{state === "saved" ? "♥" : "♡"}</span>{" "}
        {state === "saved"
          ? t.saved
          : state === "anonymous"
            ? t.signInToSave
            : t.save}
      </button>
      {error ? <p role="alert">{t.wishlistError}</p> : null}
    </div>
  );
}

export function ProductReviewsSection({
  slug,
  locale,
  navigate,
}: {
  slug: string;
  locale: Locale;
  navigate: (path: string) => void;
}) {
  const t = copy[locale];
  const [attempt, setAttempt] = useState(0);
  const [reviews, setReviews] = useState<ProductReviews | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [mine, setMine] = useState<MyReview | "anonymous" | null>(null);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<
    "idle" | "saving" | "saved" | "removed" | "error" | "forbidden"
  >("idle");

  useEffect(() => {
    let active = true;
    setLoadFailed(false);
    getProductReviews(slug)
      .then((value) => active && setReviews(value))
      .catch(() => active && setLoadFailed(true));
    getMyReview(slug)
      .then((value) => {
        if (!active) return;
        if (!value.authenticated) {
          setMine("anonymous");
          return;
        }
        setMine(value);
        setRating(value.review?.rating ?? 0);
        setTitle(value.review?.title ?? "");
        setBody(value.review?.body ?? "");
      })
      .catch(() => active && setMine("anonymous"));
    return () => {
      active = false;
    };
  }, [slug, attempt]);

  const refresh = () => setAttempt((value) => value + 1);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === "saving" || !rating || !body.trim()) return;
    setStatus("saving");
    try {
      await saveMyReview(slug, { rating, title, body });
      setStatus("saved");
      refresh();
    } catch (failure) {
      setStatus(
        failure instanceof Error &&
          failure.message === "REVIEW_PURCHASE_REQUIRED"
          ? "forbidden"
          : "error",
      );
    }
  };
  const remove = async () => {
    if (status === "saving") return;
    setStatus("saving");
    try {
      await deleteMyReview(slug);
      setRating(0);
      setTitle("");
      setBody("");
      setStatus("removed");
      refresh();
    } catch {
      setStatus("error");
    }
  };

  const summary = reviews?.summary;
  return (
    <section
      className="product-reviews page section"
      aria-labelledby="product-reviews-title"
    >
      <h2 id="product-reviews-title">{t.reviewsTitle}</h2>
      {loadFailed ? (
        <p role="alert">
          {t.loadError}{" "}
          <button type="button" className="text-link" onClick={refresh}>
            {t.retry}
          </button>
        </p>
      ) : !reviews ? null : summary && summary.count ? (
        <>
          <p className="engagement-summary">
            <Stars
              value={Math.round(summary.average)}
              label={`${summary.average} ${t.outOf}`}
            />{" "}
            <strong>{summary.average}</strong> {t.outOf} ·{" "}
            {t.reviewCount(summary.count)}
          </p>
          <ul className="engagement-review-list">
            {reviews.reviews.map((review) => (
              <li key={review.publicId}>
                <Stars value={review.rating} label={t.star(review.rating)} />
                {review.title ? <strong>{review.title}</strong> : null}
                <p>{review.body}</p>
                <small>
                  {new Date(review.createdAt).toLocaleDateString(
                    locale === "ar" ? "ar-EG" : "en-GB",
                  )}
                </small>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>{t.noReviews}</p>
      )}

      {mine === "anonymous" ? (
        <p className="engagement-note">
          {t.signInToReview}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => navigate(loginPath(`/products/${slug}`))}
          >
            {t.signIn}
          </button>
        </p>
      ) : mine && !mine.eligible && !mine.review ? (
        <p className="engagement-note">{t.purchaseRequired}</p>
      ) : mine ? (
        <form
          className="engagement-form"
          onSubmit={(event) => void submit(event)}
        >
          <h3>{t.yourReview}</h3>
          {mine.review?.state === "HIDDEN" ? (
            <p role="status">{t.hidden}</p>
          ) : null}
          <fieldset>
            <legend>{t.rating}</legend>
            <div className="engagement-rating-input">
              {[1, 2, 3, 4, 5].map((value) => (
                <label key={value}>
                  <input
                    type="radio"
                    name="rating"
                    value={value}
                    checked={rating === value}
                    onChange={() => setRating(value)}
                  />
                  <span>{t.star(value)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label>
            <span>{t.titleLabel}</span>
            <input
              value={title}
              maxLength={120}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label>
            <span>{t.bodyLabel}</span>
            <textarea
              value={body}
              rows={4}
              maxLength={2000}
              required
              onChange={(event) => setBody(event.target.value)}
            />
          </label>
          <div className="engagement-form__actions">
            <button
              className="button button--dark"
              type="submit"
              disabled={status === "saving" || !rating || !body.trim()}
            >
              {status === "saving"
                ? t.submitting
                : mine.review
                  ? t.update
                  : t.submit}
            </button>
            {mine.review ? (
              <button
                type="button"
                className="button button--outline"
                disabled={status === "saving"}
                onClick={() => void remove()}
              >
                {t.remove}
              </button>
            ) : null}
          </div>
          <div role="status" aria-live="polite">
            {status === "saved" ? <p>{t.thanks}</p> : null}
            {status === "removed" ? <p>{t.removed}</p> : null}
          </div>
          {status === "error" ? <p role="alert">{t.saveError}</p> : null}
          {status === "forbidden" ? (
            <p role="alert">{t.purchaseRequired}</p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

type WishlistEntry = {
  slug: string;
  title: string;
  image: string | null;
  imageAlt: string;
};

export function CustomerWishlist({
  locale,
  navigate,
}: {
  locale: Locale;
  navigate: (path: string) => void;
}) {
  const t = copy[locale];
  const [entries, setEntries] = useState<WishlistEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setEntries(null);
    setFailed(false);
    (async () => {
      const wishlist = await getWishlist();
      if (!wishlist.authenticated) throw new Error("CUSTOMER_SESSION_INVALID");
      const { items } = wishlist;
      const loaded = await Promise.all(
        items.map(async (item): Promise<WishlistEntry | null> => {
          try {
            const product: PublicCatalogProduct = await getProduct(
              item.slug,
              locale,
            );
            const image =
              product.media.find((media) => media.primary) ?? product.media[0];
            return {
              slug: item.slug,
              title: product.title,
              image: image?.url ?? null,
              imageAlt: image?.alt ?? "",
            };
          } catch {
            return null;
          }
        }),
      );
      if (active)
        setEntries(loaded.filter((entry): entry is WishlistEntry => !!entry));
    })().catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [locale]);
  const remove = async (slug: string) => {
    try {
      await removeFromWishlist(slug);
      setEntries(
        (current) => current?.filter((entry) => entry.slug !== slug) ?? current,
      );
    } catch {
      setFailed(true);
    }
  };
  return (
    <section aria-labelledby="wishlist-title" className="account-section">
      <h1 id="wishlist-title">{t.wishlistTitle}</h1>
      {failed ? <p role="alert">{t.wishlistLoadError}</p> : null}
      {entries === null && !failed ? (
        <p role="status">{t.loadingWishlist}</p>
      ) : null}
      {entries && !entries.length ? (
        <p>
          {t.wishlistEmpty}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => navigate("/catalog")}
          >
            {t.browse}
          </button>
        </p>
      ) : null}
      {entries?.length ? (
        <ul className="engagement-wishlist">
          {entries.map((entry) => (
            <li key={entry.slug}>
              {entry.image ? (
                <img
                  src={entry.image}
                  alt={entry.imageAlt}
                  width="96"
                  height="96"
                />
              ) : null}
              <a
                href={`/products/${entry.slug}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/products/${entry.slug}`);
                }}
              >
                {entry.title}
              </a>
              <button
                type="button"
                className="text-link"
                onClick={() => void remove(entry.slug)}
              >
                {t.removeFromWishlist}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
